import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { Mail, Copy, Plus, Trash2, RefreshCw, Loader, Save, ChevronDown } from 'lucide-react';

/* [PATCH mail-vhod-D2 v1] Stran ePošta — e-poštni vhod prejetih računov.
   Naslov <mail_koda>@racuni.alfaberg.si, bela lista pošiljateljev, seznam
   prejetih sporočil s prilogami (ime + izid vsake priponke) in filtrom.
   Vloga stranka: belo listo ureja, kode NE (backend 403 + deps vzorec brez /koda). */

type Dovoljeni = { id: string; vzorec: string; vrsta: string; aktiven: boolean };
type Priloga = { ime: string; izid?: string; razlog?: string | null; vir?: string; velikost?: number };
type SopPostavka = { zap: number; status: string; interno_st: number | null; partner: string | null; st_racuna: string | null; napaka: string | null };
type Sporocilo = {
  id: string; from_naslov: string; zadeva: string; status: string;
  razlog: string | null; prejeto_at: string | null;
  priloge: Priloga[]; document_ids: string[]; batch_id: string | null;
  batch_status?: string | null; sop_postavke?: SopPostavka[];
};

const STATUS_BARVE: Record<string, string> = {
  uvozeno: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  zavrnjen_posiljatelj: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  neznan_naslov: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  brez_prilog: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  napaka: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  v_obdelavi: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};
const STATUS_LABEL: Record<string, string> = {
  uvozeno: 'uvoženo', zavrnjen_posiljatelj: 'zavrnjen pošiljatelj',
  neznan_naslov: 'neznan naslov', brez_prilog: 'brez prilog',
  napaka: 'napaka', v_obdelavi: 'v obdelavi',
};
const IZID_BARVE: Record<string, string> = {
  uvozen: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  sop: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  preskok: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const IZID_LABEL: Record<string, string> = {  // [PATCH mail-vhod-sumniki v1] prikaz; v bazi ostaneta ASCII ključa
  uvozen: 'uvožen', sop: 'šop', preskok: 'preskok',
};

const izidBarva = (izid?: string) =>
  IZID_BARVE[izid || ''] || (izid && izid.startsWith('HTTP')
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : IZID_BARVE.preskok);

export function MailVhodPage() {
  const { selectedCompany } = useAuthStore();
  const companyId = selectedCompany?.id || '';
  const vloga = (useAuthStore.getState() as any)?.role;
  const jeStranka = vloga === 'stranka';

  const [nalagam, setNalagam] = useState(true);
  const [koda, setKoda] = useState('');
  const [naslov, setNaslov] = useState<string | null>(null);
  const [domena, setDomena] = useState('racuni.alfaberg.si');
  const [dovoljeni, setDovoljeni] = useState<Dovoljeni[]>([]);
  const [sporocila, setSporocila] = useState<Sporocilo[]>([]);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState('vsi');
  const [novVzorec, setNovVzorec] = useState('');
  const [novaVrsta, setNovaVrsta] = useState('naslov');
  const [delam, setDelam] = useState(false);

  const nalozi = async (novLimit = limit, tiho = false) => {
    if (!companyId) return;
    try {
      const [n, s] = await Promise.all([
        api.get(`/mail-vhod/${companyId}/nastavitve`),
        api.get(`/mail-vhod/${companyId}/sporocila?limit=${novLimit}`),
      ]);
      setKoda(n.data.mail_koda || '');
      setNaslov(n.data.naslov);
      setDomena(n.data.domena);
      setDovoljeni(n.data.dovoljeni || []);
      setSporocila(s.data || []);
    } catch (e: any) {
      if (!tiho) toast.error(e?.response?.data?.detail || 'Nalaganje ni uspelo');
    }
    setNalagam(false);
  };
  useEffect(() => { nalozi(); }, [companyId]);
  useEffect(() => {   // [PATCH mail-vhod-osvezi v1] samodejno osveževanje na 60 s (tiho — brez toastov ob prehodni napaki)
    if (!companyId) return;
    const t = setInterval(() => { nalozi(limit, true); }, 60000);
    return () => clearInterval(t);
  }, [companyId, limit]);

  const shraniKodo = async () => {
    setDelam(true);
    try {
      const r = await api.put(`/mail-vhod/${companyId}/koda`, { mail_koda: koda || null });
      setNaslov(r.data.naslov);
      toast.success(r.data.naslov ? `Naslov: ${r.data.naslov}` : 'Koda izbrisana');
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Kode ni bilo mogoče shraniti'); }
    setDelam(false);
  };

  const kopiraj = () => {
    if (!naslov) return;
    navigator.clipboard.writeText(naslov)
      .then(() => toast.success('Naslov kopiran'))
      .catch(() => toast.error('Kopiranje ni uspelo'));
  };

  const dodaj = async (vzorec: string, vrsta: string) => {
    if (!vzorec.trim()) return false;
    try {
      await api.post(`/mail-vhod/${companyId}/dovoljeni`, { vzorec: vzorec.trim(), vrsta });
      return true;
    } catch (e: any) {
      if (e?.response?.status === 409) return true;
      toast.error(e?.response?.data?.detail || 'Dodajanje ni uspelo');
      return false;
    }
  };

  const dodajRocno = async () => {
    setDelam(true);
    if (await dodaj(novVzorec, novaVrsta)) { setNovVzorec(''); await nalozi(); }
    setDelam(false);
  };

  const odstrani = async (id: string) => {
    try { await api.delete(`/mail-vhod/${companyId}/dovoljeni/${id}`); await nalozi(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Brisanje ni uspelo'); }
  };

  const dovoliInObdelaj = async (sp: Sporocilo) => {
    setDelam(true);
    if (await dodaj(sp.from_naslov, 'naslov')) {
      try {
        const r = await api.post(`/mail-vhod/${companyId}/sporocila/${sp.id}/obdelaj-znova`);
        toast.success(r.data?.sporocilo || 'V vrsti za ponovno obdelavo');
      } catch (e: any) { toast.error(e?.response?.data?.detail || 'Ponovna obdelava ni uspela'); }
      await nalozi();
    }
    setDelam(false);
  };

  const naloziVec = async () => { const n = Math.min(limit + 50, 200); setLimit(n); await nalozi(n); };

  const prikazana = sporocila.filter(sp => filter === 'vsi' || sp.status === filter);

  if (nalagam) return <div className="flex justify-center py-20"><Loader className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="w-6 h-6 text-cyan-500" /> ePošta
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Prejeti računi po e-pošti — samodejni uvoz vsakih 5 minut</p>
      </div>

      {/* Naslov podjetja */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-white">Naslov za prejemanje</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Koda podjetja</label>
            <div className="flex items-center gap-1">
              <input value={koda} onChange={e => setKoda(e.target.value.toLowerCase())}
                disabled={jeStranka} placeholder="npr. andi"
                className="w-36 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:border-cyan-500 dark:text-white transition disabled:opacity-60" />
              <span className="text-sm text-gray-500 dark:text-gray-400">@{domena}</span>
            </div>
          </div>
          {!jeStranka && (
            <button onClick={shraniKodo} disabled={delam}
              className="flex items-center gap-1.5 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition">
              {delam ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Shrani kodo
            </button>
          )}
          {naslov && (
            <button onClick={kopiraj}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium transition">
              <Copy className="w-4 h-4" /> {naslov}
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Stranka (ali njen dobavitelj) pošlje oz. posreduje račun na ta naslov. Sprejmejo se SAMO
          pošiljatelji s spodnjega seznama; eračuni (tudi XML, skrit v PDF ali ZIP) se preberejo
          neposredno, navadni PDF-i gredo skozi OCR.
        </p>
      </div>

      {/* Bela lista */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-2">
        <h2 className="font-semibold text-gray-900 dark:text-white">Dovoljeni pošiljatelji</h2>
        {dovoljeni.length === 0 && (
          <p className="text-sm text-amber-600 dark:text-amber-400">Seznam je prazen — noben mail se ne bo uvozil.</p>
        )}
        {dovoljeni.map(d => (
          <div key={d.id} className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
            <span className="text-gray-800 dark:text-gray-200">{d.vzorec}
              {d.vrsta === 'domena' && <span className="ml-2 text-xs text-gray-400">(cela domena)</span>}
            </span>
            <button onClick={() => odstrani(d.id)} title="Odstrani"
              className="text-gray-400 hover:text-red-500 transition"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input value={novVzorec} onChange={e => setNovVzorec(e.target.value.toLowerCase())}
            placeholder={novaVrsta === 'domena' ? '*@dobavitelj.si' : 'racuni@dobavitelj.si'}
            onKeyDown={e => { if (e.key === 'Enter') dodajRocno(); }}
            className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:border-cyan-500 dark:text-white transition" />
          <select value={novaVrsta} onChange={e => setNovaVrsta(e.target.value)}
            className="px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
            <option value="naslov">naslov</option>
            <option value="domena">domena</option>
          </select>
          <button onClick={dodajRocno} disabled={delam || !novVzorec.trim()}
            className="flex items-center gap-1 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition">
            <Plus className="w-4 h-4" /> Dodaj
          </button>
        </div>
      </div>

      {/* Sporočila */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-gray-900 dark:text-white">Prejeta sporočila
            <span className="ml-2 text-xs font-normal text-gray-400">{prikazana.length} / {sporocila.length}</span>
          </h2>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
              <option value="vsi">vsi statusi</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button type="button" onClick={() => nalozi()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
              <RefreshCw className="w-3.5 h-3.5" /> Osveži
            </button>
          </div>
        </div>

        {prikazana.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Ni sporočil.</p>}
        {prikazana.map(sp => (
          <div key={sp.id} className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl space-y-1.5">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className={`px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_BARVE[sp.status] || STATUS_BARVE.brez_prilog}`}>
                {STATUS_LABEL[sp.status] || sp.status}
              </span>
              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {sp.prejeto_at ? new Date(sp.prejeto_at).toLocaleString('sl-SI') : ''}
              </span>
              <span className="text-gray-800 dark:text-gray-200 font-medium truncate">{sp.from_naslov}</span>
              <span className="text-gray-600 dark:text-gray-300 truncate">{sp.zadeva || '(brez zadeve)'}</span>
              {sp.status === 'zavrnjen_posiljatelj' && (
                <button onClick={() => dovoliInObdelaj(sp)} disabled={delam}
                  className="ml-auto whitespace-nowrap px-2 py-1 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg font-medium transition">
                  Dovoli + obdelaj
                </button>
              )}
            </div>
            {(sp.priloge || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sp.priloge.map((p, i) => (
                  <span key={i} title={p.razlog || p.vir || ''}
                    className={`px-2 py-0.5 rounded-md text-xs ${izidBarva(p.izid)}`}>
                    {p.ime}{p.izid ? ` · ${IZID_LABEL[p.izid] || p.izid}` : ''}
                  </span>
                ))}
              </div>
            )}
            {sp.razlog && <p className="text-xs text-gray-500 dark:text-gray-400">{sp.razlog}</p>}
            {(sp.sop_postavke || []).length > 0 && (  /* [PATCH mail-vhod-D4 v1] izid vsakega kosa šopa */
              <div className="space-y-0.5">
                {(sp.sop_postavke || []).map(it => (
                  <p key={it.zap} className={'text-xs ' + (it.status === 'koncano'
                    ? 'text-green-600 dark:text-green-400'
                    : it.status === 'preskocen'
                      ? 'text-amber-600 dark:text-amber-400'
                      : ['v_vrsti', 'v_obdelavi'].includes(it.status)
                        ? 'text-blue-500 dark:text-blue-400'
                        : 'text-red-500 dark:text-red-400')}>
                    {it.status === 'koncano'
                      ? `✓ uvožen — ${it.partner || ''} (interna št. ${it.interno_st ?? '?'})`
                      : it.status === 'preskocen'
                        ? `⚠ ${(it.napaka || '').includes('že uvožen') ? 'PODVOJEN — ' : 'preskočen — '}${it.napaka || ''}`
                        : ['v_vrsti', 'v_obdelavi'].includes(it.status)
                          ? '… v obdelavi'
                          : `✗ napaka — ${it.napaka || ''}`}
                  </p>
                ))}
              </div>
            )}
            {sp.batch_id && ['priprava', 'razrez', 'v_vrsti'].includes(sp.batch_status || '') && (
              <p className="text-xs text-blue-500 dark:text-blue-400">… šop se še obdeluje</p>
            )}
          </div>
        ))}

        {sporocila.length >= limit && limit < 200 && (
          <button onClick={naloziVec}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
            <ChevronDown className="w-4 h-4" /> Naloži več (do 200)
          </button>
        )}
      </div>
    </div>
  );
}
